import { Controller } from "@hotwired/stimulus";
import { openFilePickerAfterKeyboard } from "../helpers/file_picker";

export default class extends Controller {
  static targets = ["previewContainer", "note", "submit", "hiddenTextarea", "timelineForm", "timelineIdsCreated", "noteInputFileUploadContainer"];

  filesToUpload = [];

  connect() {
    this.fileArray = [];
    this.scrollTimelineToBottom();
  }

  timelineFormTargetConnected() {
    this.previewContainerTarget.innerHTML = "";
    this.scrollTimelineToBottom();
  }

  // Runs on the plain `submit` event, which fires before Turbo builds the
  // request body — fields added during turbo:submit-start are serialized too
  // late to be sent, so the pending_file_uploads flag must be set here.
  prepareSubmit(event) {
    const inputs = this.noteInputFileUploadContainerTarget.querySelectorAll('.file-upload-input');
    const signedIds = [];
    let stillUploading = false;
    inputs.forEach((input) => {
      if (input.value) {
        signedIds.push(input.value);
      } else {
        stillUploading = true;
      }
    });

    if (stillUploading) {
      event.preventDefault();
      alert("Files are still uploading. Please wait until they finish before sending.");
      return;
    }

    this.filesToUpload = signedIds;

    // Tell the server "files are coming in a second request" so it can skip
    // the uploadFile in-app notification. The second request
    // (/file_uploads/upload_timeline_files) fires the uploadFile notification
    // once the files actually finish uploading.
    if (this.filesToUpload.length > 0 && this.hasTimelineFormTarget) {
      const form = this.timelineFormTarget
      let flag = form.querySelector('input[name="pending_file_uploads"]')
      if (!flag) {
        flag = document.createElement('input')
        flag.type = 'hidden'
        flag.name = 'pending_file_uploads'
        form.appendChild(flag)
      }
      flag.value = 'true'
    }
  }

  submitStart() {
    this.submitTarget.disabled = true;
    this.submitTarget
      .querySelector(".spinner-border")
      ?.classList.remove("d-none");
    this.submitTarget.querySelector("span")?.classList.add("d-none");
    // Mobile composer: icon-only button, swap the icon for a spinner (the
    // frame is re-rendered by the server after the post, so no reset needed).
    if (this.submitTarget.classList.contains("mobile-chat-send-btn")) {
      this.submitTarget.innerHTML = '<span class="spinner-border" role="status" style="width:14px;height:14px;min-width:14px;min-height:14px;border-width:2px;flex-shrink:0"></span>';
    }
    this.noteTarget.disabled = true;
  }

  // ── Mobile composer (referrals/_note_input_section.html+mobile.erb) ──
  // Same behavior as the chat composer (message_form_controller): the
  // textarea grows with its content, Enter posts, Shift+Enter is a newline,
  // and the send icon lights up once there is a note or a finished upload.

  autoResize() {
    const textarea = this.noteTarget;
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    this.updateSendButton();
  }

  handleKeydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    if (this.submitTarget.disabled || this.hasPendingUploads() || !this.canSend()) return;
    this.timelineFormTarget.requestSubmit();
  }

  // See message_form_controller#openFilePicker.
  openFilePicker(event) {
    if (event) event.preventDefault();
    const input = document.getElementById("timeline_files");
    if (input) openFilePickerAfterKeyboard(input);
  }

  canSend() {
    return this.noteTarget.value.trim().length > 0 || this.element.querySelectorAll(".file-wrapper--completed").length > 0;
  }

  hasPendingUploads() {
    return this.element.querySelector(".file-wrapper--uploading") !== null;
  }

  updateSendButton() {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.classList.toggle("btn-send-active", this.canSend());
  }

  timelineIdsCreatedTargetConnected(e) {
    const senderReferralTimelineId = e.dataset.senderReferralTimelineId;
    const receiverReferralTimelineId = e.dataset.receiverReferralTimelineId;

    if (this.filesToUpload.length > 0) {
      this.attachFiles(senderReferralTimelineId, receiverReferralTimelineId, this.filesToUpload);
      this.filesToUpload = [];
    }
  }

  attachFiles(senderReferralTimelineId, receiverReferralTimelineId, signedIds) {
    const fieldUploadForm = document.querySelector('form#timeline-file-upload-form');

    fieldUploadForm.querySelectorAll('input[name="file_upload[files][]"]').forEach(el => el.remove());

    signedIds.forEach((signedId) => {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'file_upload[files][]';
      hidden.value = signedId;
      fieldUploadForm.appendChild(hidden);
    });

    fieldUploadForm.action = "/file_uploads/upload_timeline_files";
    fieldUploadForm.querySelector('input[name="sender_referral_timeline_id"]').value = senderReferralTimelineId;
    fieldUploadForm.querySelector('input[name="receiver_referral_timeline_id"]').value = receiverReferralTimelineId;

    fieldUploadForm.requestSubmit();
  }

  scrollTimelineToBottom() {
    const container = document.querySelector(".timeline-messages");
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }

  displayNote() {
    const textarea = this.noteTarget;
    const minRows = parseInt(textarea.dataset.initRows || 1);
    const maxRows = parseInt(textarea.dataset.limitRows || 5);

    // Reset to min rows to get an accurate scrollHeight
    textarea.rows = minRows;
    const style = getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);
    const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom;
    const newRows = Math.min(Math.max(Math.ceil(contentHeight / lineHeight), minRows), maxRows);
    textarea.rows = newRows;
  }
}
